import { Container, Section } from '@components';
import usePageMeta from '@/utils/usePageMeta';

const LAST_UPDATED = 'July 8, 2026';

const SectionHeading = ({ children }) => (
  <h2 className="text-h4 font-bold text-primary-navy mt-10 mb-3">{children}</h2>
);

const Paragraph = ({ children }) => (
  <p className="text-neutral-charcoal leading-relaxed mb-4">{children}</p>
);

const BulletList = ({ items }) => (
  <ul className="list-disc list-inside space-y-2 mb-4 text-neutral-charcoal">
    {items.map((item, i) => (
      <li key={i} className="leading-relaxed">{item}</li>
    ))}
  </ul>
);

const TermsOfService = () => {
  usePageMeta({
    title: 'Terms of Service | DataLogicHub',
    description:
      'Read the terms and conditions that govern your use of DataLogicHub, the AI-powered content generation and analytics platform.',
  });

  return (
    <>
      {/* Hero */}
      <Section className="bg-primary-navy text-white py-16">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-h2 font-bold mb-4">Terms of Service</h1>
            <p className="text-white/80 text-lg">
              These terms govern your access to and use of DataLogicHub. By
              using our services, you agree to be bound by them.
            </p>
            <p className="mt-4 text-sm text-primary-teal font-medium">
              Last updated: {LAST_UPDATED}
            </p>
          </div>
        </Container>
      </Section>

      {/* Content */}
      <Section className="py-12 bg-white">
        <Container>
          <div className="max-w-3xl mx-auto">

            <SectionHeading>1. Acceptance of Terms</SectionHeading>
            <Paragraph>
              By accessing or using DataLogicHub (the &quot;Service&quot;), you
              agree to these Terms of Service and our Privacy Policy. If you do
              not agree, do not use the Service. We may update these terms from
              time to time; continued use after changes take effect constitutes
              acceptance of the revised terms.
            </Paragraph>

            <SectionHeading>2. Accounts and Registration</SectionHeading>
            <BulletList items={[
              'You must provide accurate and complete information when creating an account.',
              'You are responsible for safeguarding your account credentials and for all activity under your account.',
              'You must notify us promptly of any unauthorised use of your account.',
              'We may suspend or terminate accounts that violate these terms.',
            ]} />

            <SectionHeading>3. Use of the Service</SectionHeading>
            <Paragraph>
              You agree to use the Service only for lawful purposes. You must not:
            </Paragraph>
            <BulletList items={[
              'Use the Service to generate or distribute unlawful, harmful, or misleading content.',
              'Attempt to gain unauthorised access to the Service or its related systems.',
              'Interfere with or disrupt the integrity or performance of the Service.',
              'Resell, sublicense, or misuse the Service or its outputs without permission.',
              'Use automated means to scrape or extract data from the Service beyond documented APIs.',
            ]} />

            <SectionHeading>4. Content and Intellectual Property</SectionHeading>
            <BulletList items={[
              'You retain ownership of content you create or upload to the Service.',
              'You grant DataLogicHub a limited licence to host, process, and display your content solely to operate the Service.',
              'AI-generated content is provided as-is; you are responsible for reviewing it before publication.',
              'The Service itself, including its design, code, and branding, remains the property of DataLogicHub.',
            ]} />

            <SectionHeading>5. Paid Services</SectionHeading>
            <Paragraph>
              Certain features may require payment. Prices and features are
              described at the point of purchase. Fees are non-refundable except
              where required by law. We may change pricing with reasonable
              advance notice.
            </Paragraph>

            <SectionHeading>6. Disclaimers</SectionHeading>
            <Paragraph>
              The Service is provided &quot;as is&quot; and &quot;as
              available&quot; without warranties of any kind, express or
              implied. We do not warrant that the Service will be uninterrupted,
              error-free, or that AI-generated outputs will be accurate or fit
              for any particular purpose.
            </Paragraph>

            <SectionHeading>7. Limitation of Liability</SectionHeading>
            <Paragraph>
              To the maximum extent permitted by law, DataLogicHub shall not be
              liable for any indirect, incidental, special, consequential, or
              punitive damages, or any loss of profits, data, or goodwill,
              arising from your use of the Service.
            </Paragraph>

            <SectionHeading>8. Termination</SectionHeading>
            <Paragraph>
              You may stop using the Service at any time. We may suspend or
              terminate your access if you breach these terms. Upon termination,
              your right to use the Service ceases immediately; sections that by
              their nature should survive termination will survive.
            </Paragraph>

            <SectionHeading>9. Contact</SectionHeading>
            <Paragraph>
              Questions about these terms? Reach out through our{' '}
              <a href="/work-with-me" className="text-primary-teal hover:underline">
                contact page
              </a>
              .
            </Paragraph>

          </div>
        </Container>
      </Section>
    </>
  );
};

export default TermsOfService;
